<template>
<div class="content-block">
    <h2>Raffle List</h2>
    <div v-for="raffle in raffleList" :key="raffle.id">
        <div><router-link :to="`/raffle/${raffle.type}`">{{ raffle.label }}</router-link></div>
    </div>
    <div v-if="!raffleList || !raffleList.length">
        <div>(No raffles found)</div>
    </div>
</div>
<div class="content-block dx-card" style="padding:20px;">
    <DxDataGrid
        :data-source="dataItemsSource"
        @editor-preparing="onEditorPreparing"
        @row-expanding="onRowExpanding"
    >
        <DxEditing :allow-updating="true" :allow-deleting="true" :allow-adding="true" mode="form" />
        <DxColumn data-field="id" caption="ID" :allow-editing="false" />
        <DxColumn data-field="type" caption="Type" />
        <DxColumn data-field="label" caption="Label" />
        <DxColumn data-field="draws" caption="Draws" />
		<DxColumn data-field="survey_count" caption="Surveys" :allow-editing="false" />
        <DxColumn data-field="winners" caption="Winners" data-type="object" />
        <DxColumn data-field="created_at" caption="Created" :allow-editing="false" />
        <DxColumn data-field="updated_at" caption="Updated" :allow-editing="false" />
		<DxColumn type="buttons" width="160">
            <DxGridButton name="edit" />
            <DxGridButton name="delete" />
            <DxGridButton hint="Manual import" icon="upload" :on-click="onManualImportClick" />
        </DxColumn>
        <DxMasterDetail :enabled="true" template="winnerDetail" />
        <template #winnerDetail="{ data }">
            <div style="padding: 10px 0;">
                <div v-if="isWinnersLoading(getRowId(data))">Loading winners…</div>
                <div v-else-if="getWinnersError(getRowId(data))">Unable to load winners.</div>
                <div v-else>
                    <DxDataGrid
                        :key="`winners-grid-${getRowId(data)}`"
                        :data-source="getWinnersForRow(getRowId(data))"
                        :show-borders="true"
                        :column-auto-width="true"
                        @exporting="onWinnersExporting"
                        :element-attr="{ 'data-raffle-id': getRowId(data) }"
                    >
                        <DxSearchPanel :visible="true" :highlight-case-sensitive="false" :highlight-search-text="true" :width="280" placeholder="Search winners..." />
                        <DxToolbar>
                            <DxItem name="searchPanel" location="before" />
                            <DxItem name="exportButton" location="after" />
                            <DxItem location="after" widget="dxButton" :options="getRefreshButtonOptions(getRowId(data))" />
                        </DxToolbar>
                        <DxExport :enabled="true" :formats="['xlsx', 'csv']" />
                    </DxDataGrid>
                </div>
            </div>
        </template>
    </DxDataGrid>
</div>
</template>

<script setup>
/* eslint-disable */
import { inject, ref } from 'vue';
import DxDataGrid, { DxColumn, DxEditing, DxButton as DxGridButton, DxMasterDetail, DxToolbar, DxItem, DxSearchPanel, DxExport } from "devextreme-vue/data-grid";
import DataSource from 'devextreme/data/data_source';
import { useRouter } from 'vue-router';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';

const $http = inject('$http');
const raffleList = ref([]);
const router = useRouter();

const dataItemsSource = new DataSource({
	loadMode: 'raw',
	cacheRawData: true,
	key: 'id',
	load: async (loadOptions) => {
		let fetchRequest = await $http.get('/raffle');
		raffleList.value = fetchRequest.data;
		return fetchRequest.data;
	},
	insert: async (values) => {
        const response = await $http.post('/raffle', values);
		return response.data;
	},
	update: async (key, values) => {
		const originalItem = dataItemsSource['_items'].find(item => item.id == key);
		if (!originalItem) {
			throw new Error('Could not find original item for update; save aborted!');
		}
		const patchData = {
			id: key,
		};
		for (var [k, v] of Object.entries(values)) {
            if (k == 'winners') {
                if (!v || v == '' || v == '[]') {
                    v = []; 
                } else {
                    v = v.split(',');
                }
            }
			patchData[k] = v;
		}
		const response = await $http.patch(`/raffle/${key}`, patchData);
		return response.data;
	},
	remove: async (key) => {
		const response = await $http.delete(`/raffle/${key}`);
		return response.data;
	}
});

const onEditorPreparing = (e) => {
    if (e.dataField === 'type' && e.parentType === 'dataRow' && !e.row.isNewRow) {
		e.editorOptions.disabled = true;
	}
};

const onManualImportClick = (e) => {
	const rowData = e.row && e.row.data ? e.row.data : null;
	if (!rowData) {
		return;
	}
	router.push('/manage/results');
};

// Master-detail: winners per raffle
const winnersById = ref({});

const ensureWinnersLoaded = async (id, force = false) => {
	if (!id) { return; }
	const state = winnersById.value[id] || { loading: false, loaded: false, error: null, items: [] };
	if (!force && (state.loading || state.loaded)) { return; }
	winnersById.value[id] = { ...state, loading: true, error: null };
	try {
		const res = await $http.get(`/raffle/${id}/winners`);
		const items = Array.isArray(res.data) ? res.data : (res.data && Array.isArray(res.data.winners) ? res.data.winners : []);
		winnersById.value[id] = { loading: false, loaded: true, error: null, items };
	} catch (ex) {
		winnersById.value[id] = { loading: false, loaded: false, error: ex, items: [] };
	}
};

const onRowExpanding = (e) => {
	const id = e && (e.key ?? (e.data && e.data.id));
	if (!id) { return; }
	ensureWinnersLoaded(id);
};

const getRowId = (slotData) => {
	return slotData && slotData.data ? slotData.data.id : (slotData && slotData.id ? slotData.id : null);
};

const getWinnersForRow = (id) => {
	return (id && winnersById.value[id] && winnersById.value[id].items) ? winnersById.value[id].items : [];
};

const isWinnersLoading = (id) => {
	return !!(id && winnersById.value[id] && winnersById.value[id].loading);
};

const getWinnersError = (id) => {
	return id && winnersById.value[id] ? winnersById.value[id].error : null;
};

const refreshWinnersForRow = async (id) => {
	if (!id) { return; }
	const existing = winnersById.value[id] || { items: [] };
	winnersById.value[id] = { ...existing, loading: true, error: null, loaded: false };
	await ensureWinnersLoaded(id, true);
};

const getRefreshButtonOptions = (id) => ({
	icon: 'refresh',
	hint: 'Refresh',
	onClick: () => refreshWinnersForRow(id),
	disabled: isWinnersLoading(id),
});

const onWinnersExporting = (e) => {
    const idAttr = e.component && e.component.option ? e.component.option('elementAttr') : null;
    const raffleId = idAttr && (idAttr['data-raffle-id'] ?? idAttr['data-raffle-id'.toString()]);
    const dateCode = (new Date()).toISOString().substring(0, 10);

    if (e.format === 'csv') {
        e.cancel = true;
        const columns = (e.component.getVisibleColumns && e.component.getVisibleColumns()) || [];
        const dataSource = e.component.getDataSource && e.component.getDataSource();
        const items = (dataSource && dataSource.items && dataSource.items()) || [];
        const headers = columns.map(c => (c.caption || c.dataField || '').toString());
        const csvEscape = (val) => {
            const s = val == null ? '' : String(val);
            const needsQuotes = /[",\n]/.test(s);
            const escaped = s.replace(/"/g, '""');
            return needsQuotes ? '"' + escaped + '"' : escaped;
        };
        const rows = items.map(row => columns.map(c => csvEscape(c.calculateCellValue ? c.calculateCellValue(row) : row[c.dataField])));
        const csvContent = [headers.map(csvEscape).join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
        const suffix = raffleId != null ? `-${raffleId}` : '';
        saveAs(blob, `Winners${suffix}-${dateCode}.csv`);
        return;
    }

    if (e.format === 'xlsx') {
        e.cancel = true;
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet('Winners');
        exportDataGrid({
            component: e.component,
            worksheet: worksheet,
            customizeCell: (options) => {
                options.excelCell.font = { name: 'Arial', size: 12 };
                options.excelCell.alignment = { horizontal: 'left' };
            }
        }).then(() => {
            workbook.xlsx.writeBuffer().then((buffer) => {
                const suffix = raffleId != null ? `-${raffleId}` : '';
                saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `Winners${suffix}-${dateCode}.xlsx`);
            });
        });
    }
};

</script>

<style>

</style>